import { VStack, styled, FormRow } from '@cad-arc';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { EventPanelMetadata, EventPanelFormType, DispatchAreaOption } from '@mark43/cad-api';
import { useEventMetadataStore } from '../event-panel/event-panel-form/event-metadata';
import { useWatch } from 'react-hook-form';
import { FormTextarea } from '../forms/integrated-form-controls/form-text-area';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { FormSelect } from '../forms/integrated-form-controls/form-select';
import { FrCreateOrEditEventFormSectionTitle } from './fr-create-or-edit-event-form-section-title';
import { EventFormNarrativeInput } from '../common/components/event-form-narrative-input';
import { EventFormAdditionalCallForServiceSelect } from '../common/components/event-form-additional-call-for-service-select';
import { useEventFormCallForServiceOptions } from '../common/hooks/use-event-form-call-for-service-options';
import { EventFormLabelsSelect } from '../common/components/event-form-labels-select';
import { useGetAgencyType } from '../common/hooks/use-get-agency-type';
import { usePrefillRadioChannelBasedOnDispatchArea } from './hooks';
import { FrCreateOrEditEventLocationField } from './fr-create-or-edit-event-location-field';
import { useIntegratedForm } from '../forms/use-integrated-form';
import { useAgencyTypeGlobalAttributeId } from '../common/hooks/use-agency-type-global-attribute-id';
import { HIDDEN_SUBDIVISION_FIELDS_NAMES } from './constants';

const StyledVStack = styled(VStack)`
    align-items: flex-start;
    gap: 5px;
    height: 100%;
    width: 33%;
    padding: var(--arc-space-6) var(--arc-space-2) var(--arc-space-6) var(--arc-space-6);
    overflow-y: auto;

    @media (max-width: 1200px) {
        width: 100%;
        overflow-y: initial;
        padding: var(--arc-space-6);
    }
`;

export function FrCreateOrEditEventFormLeftContainer() {
    const agencyTypeGlobalAttrId = useAgencyTypeGlobalAttributeId();
    const { eventId } = useEventMetadataStore()?.state?.metadata ?? ({} as EventPanelMetadata);
    const { form, isSubmitting } = useIntegratedForm<EventPanelFormType>();
    const { eventPanelOptions: eventOptions } = useBootstrapState();
    const callForServiceOptions = useEventFormCallForServiceOptions();
    const prefillRadioChannelBasedOnDispatchArea = usePrefillRadioChannelBasedOnDispatchArea();
    const getAgencyType = useGetAgencyType(agencyTypeGlobalAttrId);

    const {
        eventsHaveRiskLevel,
        radioChannels: radioChannelsEnabled,
        eventLocationNotes: eventLocationNotesEnabled,
    } = useEnabledFeatures();

    const radioChannel = useWatch({
        control: form.control,
        name: `${getAgencyType()}PrimaryRadioChannel`,
    });

    return (
        <StyledVStack>
            <FrCreateOrEditEventFormSectionTitle title="Event Info" />
            <FrCreateOrEditEventLocationField />
            {/* In order to properly preserve subdivisions on eventual refreshes in React Hook Form state,
            we need to render JSX input elements for them, even though they are hidden. */}
            {HIDDEN_SUBDIVISION_FIELDS_NAMES.map((name) => (
                <input key={name} type="hidden" {...form.register(name)} />
            ))}
            <FormRow>
                <FormSelect
                    asEditable
                    asDataListItem
                    length={eventLocationNotesEnabled ? 'md' : undefined}
                    control={form.control}
                    label="D-Area"
                    name={`${getAgencyType()}DispatchArea`}
                    eventNotificationName="DISPATCH_AREA"
                    options={eventOptions?.[`${getAgencyType()}DispatchAreaOptions`] ?? []}
                    showRequiredIndicator
                    mode={isSubmitting ? 'read-only' : undefined}
                    onChange={({ target }) => {
                        prefillRadioChannelBasedOnDispatchArea(
                            // @ts-expect-error 'value" is not recognized as a property of "target"
                            (target?.value as unknown) as DispatchAreaOption
                        );
                    }}
                />
                {eventLocationNotesEnabled && (
                    <FormTextarea
                        control={form.control}
                        label="Location Notes"
                        name="locationNotes"
                        eventNotificationName="EVENT_LOCATION_NOTES"
                        asEditable
                        length="md"
                        mode={isSubmitting ? 'read-only' : undefined}
                    />
                )}
            </FormRow>
            <FormRow>
                <FormSelect
                    showRequiredIndicator
                    control={form.control}
                    label="Call For Service"
                    name="callForService"
                    eventNotificationName="CFS"
                    options={callForServiceOptions}
                    asEditable
                    mode={isSubmitting ? 'read-only' : undefined}
                />
                <EventFormAdditionalCallForServiceSelect
                    mode={isSubmitting ? 'read-only' : undefined}
                />
            </FormRow>
            <FormRow>
                {eventsHaveRiskLevel && (
                    <FormSelect
                        control={form.control}
                        label="Risk Level"
                        name="riskLevel"
                        eventNotificationName="RISK_LEVEL"
                        options={eventOptions?.riskLevelOptions || []}
                        showRequiredIndicator
                        length="md"
                        asEditable
                        mode={isSubmitting ? 'read-only' : undefined}
                    />
                )}
                <FormSelect
                    control={form.control}
                    label="Priority"
                    name="priority"
                    eventNotificationName="EVENT_PRIORITY"
                    options={eventOptions?.priorityOptions || []}
                    showRequiredIndicator
                    length="md"
                    asEditable
                    mode={isSubmitting ? 'read-only' : undefined}
                />
                <FormSelect
                    control={form.control}
                    label="Origin"
                    name="eventOrigin"
                    options={eventOptions?.eventOriginSelfInitiateOptions || []}
                    showRequiredIndicator
                    length="md"
                    asEditable
                    mode={isSubmitting ? 'read-only' : undefined}
                />
                <FormSelect
                    control={form.control}
                    label="Disp"
                    name="eventQueue"
                    options={eventOptions?.eventQueueOptions || []}
                    showRequiredIndicator
                    length="md"
                    asEditable
                    mode={isSubmitting ? 'read-only' : undefined}
                />
            </FormRow>
            <EventFormNarrativeInput
                eventId={eventId}
                mode={isSubmitting ? 'read-only' : undefined}
            />
            {radioChannelsEnabled && (
                <>
                    <FormRow>
                        <FormSelect
                            control={form.control}
                            label="Radio Channel"
                            name={`${getAgencyType()}PrimaryRadioChannel`}
                            eventNotificationName="RADIO_CHANNEL"
                            options={eventOptions?.radioChannelOptions || []}
                            asEditable
                            mode={isSubmitting ? 'read-only' : undefined}
                        />
                    </FormRow>
                    {eventId && radioChannel && (
                        <FormRow>
                            <FormSelect
                                control={form.control}
                                label="Additional Radio Channels"
                                name="additionalRadioChannels"
                                options={eventOptions?.radioChannelOptions || []}
                                isMultiple
                                truncateSelectedCount={2}
                                eventNotificationName="ADDITIONAL_RADIO_CHANNEL"
                                asEditable
                                mode={isSubmitting ? 'read-only' : undefined}
                            />
                        </FormRow>
                    )}
                </>
            )}
            <EventFormLabelsSelect mode={isSubmitting ? 'read-only' : undefined} />
        </StyledVStack>
    );
}
